import React, { useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import format from "date-fns/format";
import { Grid, Typography, Card, Box } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import ShareButton from "../../../shared/components/ShareButton";
import smoothScrollTop from "../../../shared/functions/smoothScrollTop";

const styles = (theme) => ({
  wrapper: {
    background: theme.palette.primary.backgroundAlt,
    minHeight: "100vh",
  },

  container: {
    maxWidth: "900px",
    margin: "auto",
    padding: "calc(2em + 80px) 1.5em 2em 1.5em",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),

    [theme.breakpoints.down("md")]: {
      padding: "calc(1.5em + 60px) 1em 1.5em 1em",
      gap: theme.spacing(2),
    },
  },

  headerSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },

  titleContainer: {
    background: theme.palette.mode === "light" 
      ? "rgba(255, 255, 255, 0.9)" 
      : "rgba(255, 255, 255, 0.05)",
    borderRadius: "20px",
    padding: theme.spacing(3),
    border: theme.palette.mode === "light" 
      ? "1px solid rgba(0, 0, 0, 0.06)" 
      : "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    boxShadow: theme.palette.mode === "light" 
      ? "0 8px 32px rgba(0, 0, 0, 0.06)" 
      : "0 8px 32px rgba(0, 0, 0, 0.3)",
    position: "relative",
    overflow: "hidden",

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "4px",
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    },

    "& h3": {
      fontSize: "2.2rem",
      fontWeight: 700,
      lineHeight: 1.2,
      marginBottom: theme.spacing(1.5),
      background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",

      [theme.breakpoints.down("md")]: {
        fontSize: "1.8rem",
      },
      [theme.breakpoints.down("sm")]: {
        fontSize: "1.5rem",
      },
    },
  },

  metaInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "14px",
    fontWeight: 500,

    "& svg": {
      width: "16px",
      height: "16px",
    },
  },

  videoContainer: {
    background: theme.palette.mode === "light" 
      ? "rgba(255, 255, 255, 0.95)" 
      : "rgba(255, 255, 255, 0.05)",
    borderRadius: "20px",
    padding: theme.spacing(2),
    border: theme.palette.mode === "light" 
      ? "1px solid rgba(0, 0, 0, 0.06)" 
      : "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    boxShadow: theme.palette.mode === "light" 
      ? "0 8px 32px rgba(0, 0, 0, 0.06)" 
      : "0 8px 32px rgba(0, 0, 0, 0.3)",
    position: "relative",
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },

  video: {
    width: "100%",
    display: "block",
    borderRadius: "16px",
    overflow: "hidden",
    border: "none",
    position: "relative",
    aspectRatio: "16/9",

    "& iframe": {
      width: "100%",
      height: "100%",
      borderRadius: "16px",
      border: "none",
      position: "absolute",
      top: 0,
      left: 0,
    },

    "& > div": {
      position: "relative",
      width: "100%",
      height: 0,
      paddingBottom: "56.25%", // 16:9 aspect ratio
    },

    "& .vid": {
      width: "100%",
      height: "100%",
      borderRadius: "16px",
      border: "none",
      position: "absolute",
      top: 0,
      left: 0,
    },
  },

  moreContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    background: theme.palette.mode === "light" 
      ? "rgba(255, 255, 255, 0.9)" 
      : "rgba(255, 255, 255, 0.05)",
    borderRadius: "20px",
    border: theme.palette.mode === "light" 
      ? "1px solid rgba(0, 0, 0, 0.06)" 
      : "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    boxShadow: theme.palette.mode === "light" 
      ? "0 8px 32px rgba(0, 0, 0, 0.06)" 
      : "0 8px 32px rgba(0, 0, 0, 0.3)",
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    
    "&::before": {
      content: '""',
      width: "4px",
      height: "28px",
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
      borderRadius: "2px",
    },

    "& h4": {
      fontSize: "1.5rem",
      fontWeight: 700,
      margin: 0,
      background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    },
  },

  moreBlogGrid: {
    display: "grid",
    gap: theme.spacing(3),
    gridTemplateColumns: "1fr",
  },

  moreBlogCard: {
    display: "flex",
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    background: theme.palette.mode === "light" 
      ? "rgba(255, 255, 255, 0.8)" 
      : "rgba(255, 255, 255, 0.03)",
    borderRadius: "20px",
    border: theme.palette.mode === "light" 
      ? "1px solid rgba(0, 0, 0, 0.05)" 
      : "1px solid rgba(255, 255, 255, 0.08)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}10, transparent)`,
      transition: "left 0.5s ease",
    },

    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 12px 30px rgba(${theme.palette.primary.main}, 0.15)`,
      border: `1px solid ${theme.palette.primary.main}30`,

      "&::before": {
        left: "100%",
      },

      "& .video-image": {
        transform: "scale(1.05)",
      },

      "& .video-title": {
        color: theme.palette.primary.main,
      },
    },

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: theme.spacing(2),
    },
  },

  videoImageContainer: {
    minWidth: "200px",
    width: "200px",
    height: "120px",
    borderRadius: "16px",
    overflow: "hidden",
    position: "relative",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
      minWidth: "unset",
      height: "180px",
    },

    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "transform 0.3s ease",
    },
  },

  videoContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    position: "relative",
    zIndex: 1,

    "& h5": {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.3,
      margin: 0,
      transition: "color 0.3s ease",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },

    "& p": {
      fontSize: "14px",
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
      margin: 0,
      display: "-webkit-box",
      WebkitLineClamp: 3,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
  },

  readMoreLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
    fontSize: "14px",
    fontWeight: 600,
    textDecoration: "none",
    marginTop: "auto",
    transition: "all 0.3s ease",

    "&:hover": {
      transform: "translateX(4px)",
    },

    "& svg": {
      transition: "transform 0.3s ease",
    },

    "&:hover svg": {
      transform: "translateX(4px)",
    },
  },

  btnContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
});

function VideoPost(props) {
  const { classes, date, title, content, otherArticles } = props;

  // Get 3 random articles from otherArticles for "More Like This" section
  const getRandomVideos = (articles, count = 3) => {
    const shuffled = [...articles].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  const moreVideos = getRandomVideos(otherArticles);

  // Format date
  const formatDate = (timestamp) => {
    return format(new Date(timestamp * 1000), "MMMM dd, yyyy", {
      awareOfUnicodeTokens: true,
    });
  };

  useEffect(() => {
    document.title = `Auralyze.ai - ${title}`;
    smoothScrollTop();
  }, [title]);

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.container}>
        {/* Header Section */}
        <div className={classes.headerSection}>
          <div className={classes.titleContainer}>
            <Typography variant="h3">
              {title}
            </Typography>
            <div className={classes.metaInfo}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
              </svg>
              <span>{formatDate(date)}</span>
              <span>•</span>
              <span>Video</span>
            </div>
          </div>
        </div>

        {/* Video Content */}
        <div className={classes.videoContainer}>
          <Box className={classes.video}>{content}</Box>
        </div>

        {/* More Like This Section - Only show if we have other videos */}
        {moreVideos.length > 0 && (
          <div className={classes.moreContainer}>
            <div className={classes.sectionHeader}>
              <Typography variant="h4">
                More Videos
              </Typography>
            </div>
            
            <div className={classes.moreBlogGrid}>
              {moreVideos.map((video, index) => (
                <div 
                  className={classes.moreBlogCard} 
                  key={video.id}
                  onClick={() => window.location.href = video.url}
                >
                  <div className={classes.videoImageContainer}>
                    <img 
                      src={video.src} 
                      alt={video.title}
                      className="video-image"
                    />
                  </div>
                  
                  <div className={classes.videoContent}>
                    <Typography variant="h5" className="video-title">
                      {video.title}
                    </Typography>
                    <Typography variant="body1">
                      {video.snippet}
                    </Typography>
                    <a href={video.url} className={classes.readMoreLink}>
                      Watch Video
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Box>
    </Box>
  );
}

VideoPost.propTypes = {
  classes: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  date: PropTypes.number.isRequired,
  src: PropTypes.string.isRequired,
  content: PropTypes.node.isRequired,
  otherArticles: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default withStyles(styles, { withTheme: true })(VideoPost);
